// AiAdvisory.jsx — rebuilt with ITAdvisory design system
import React, { useEffect, useRef, useState } from 'react';
import './AiAdvisory.css';
// import ai from "../../../assets/services/ai.png"; // re-enable when ready

import {
  IoBusinessOutline,
  IoPeopleOutline,
  IoShieldCheckmarkOutline,
  IoBulbOutline,
  IoAnalyticsOutline,
  IoBarChartOutline,
  IoCheckmarkCircleOutline,
  IoArrowForwardOutline,
  IoCalendarOutline,
  IoGitBranchOutline,
} from 'react-icons/io5';
import {
  FiTarget,
  FiMap,
  FiLayers,
  FiCpu,
  FiBarChart2,
  FiSettings,
} from 'react-icons/fi';
import {
  MdOutlineAutoAwesome,
  MdOutlinePsychology,
} from 'react-icons/md';

/* ── Data ─────────────────────────────────────────────── */

const achievements = [
  { title: "Define a clear enterprise AI roadmap", icon: <FiMap size={22} /> },
  { title: "Identify high-value AI use cases", icon: <FiTarget size={22} /> },
  { title: "Align AI initiatives with business strategy", icon: <IoBusinessOutline size={22} /> },
  { title: "Improve operational efficiency through AI", icon: <IoAnalyticsOutline size={22} /> },
  { title: "Establish AI governance and risk frameworks", icon: <IoShieldCheckmarkOutline size={22} /> },
  { title: "Build scalable AI operating models", icon: <FiLayers size={22} /> },
  { title: "Accelerate responsible GenAI adoption", icon: <MdOutlineAutoAwesome size={22} /> },
];

const approachSteps = [
  {
    number: "01",
    title: "Business & Operational Assessment",
    desc: "Evaluate current capabilities, identify pain points, and align AI opportunities with business objectives.",
    icon: <IoAnalyticsOutline size={26} />,
  },
  {
    number: "02",
    title: "AI Readiness Evaluation",
    desc: "Assess data maturity, technology stack, skills, and organisational readiness for AI adoption.",
    icon: <FiCpu size={26} />,
  },
  {
    number: "03",
    title: "Use Case Prioritisation",
    desc: "Identify and rank high-impact AI use cases based on business value and technical feasibility.",
    icon: <FiBarChart2 size={26} />,
  },
  {
    number: "04",
    title: "Roadmap & Execution Strategy",
    desc: "Develop a phased implementation plan with clear milestones, resources, and success metrics.",
    icon: <IoGitBranchOutline size={26} />,
  },
  {
    number: "05",
    title: "Governance & Scale",
    desc: "Establish governance frameworks, risk controls, and scalable processes for enterprise-wide AI deployment.",
    icon: <IoShieldCheckmarkOutline size={26} />,
  },
];

const whyBairees = [
  { text: "Business-first AI strategy approach", icon: <IoBusinessOutline size={22} /> },
  { text: "Focus on measurable business outcomes", icon: <IoBarChartOutline size={22} /> },
  { text: "Practical and execution-oriented advisory", icon: <FiSettings size={22} /> },
  { text: "Enterprise governance and scalability focus", icon: <IoShieldCheckmarkOutline size={22} /> },
  { text: "Deep understanding of AI transformation challenges", icon: <IoBulbOutline size={22} /> },
];

const differentiators = [
  "Turn AI ambition into business results",
  "Move beyond experimentation",
  "Scalable, responsible AI transformation",
  "Execution-focused programs",
];

const stats = [
  { number: "150+", label: "AI Roadmaps Delivered" },
  { number: "12+", label: "Industries Covered" },
  { number: "87%", label: "Use Cases Deployed" },
  { number: "4×", label: "Avg. Efficiency Gain" },
];

/* ── Counter hook ──────────────────────────────────────────── */
function useCountUp(target, duration = 1800, start = false) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const step = (ts) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      setValue(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [start, target, duration]);
  return value;
}

function StatCard({ number, label, delay, inView }) {
  // Parse number to get numeric value and suffix
  const numericValue = parseInt(number);
  const suffix = number.replace(numericValue.toString(), '');
  const count = useCountUp(numericValue, 1600, inView);
  
  return (
    <div className="da-stat" style={{ animationDelay: `${delay}ms` }}>
      <span className="da-stat__number">{count}{suffix}</span>
      <span className="da-stat__label">{label}</span>
    </div>
  );
}

/* ── Component ────────────────────────────────────────── */

const AiAdvisory = () => {
  const statsRef = useRef(null);
  const [statsVisible, setStatsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsVisible(true); },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  // RETURN statement was missing!
  return (
    <div className="ita">

      {/* ── HERO ─────────────────────────────────────────── */}
      <section className="ita-hero">
        <div className="ita-hero__bg-grid" />

        <div className="ita-hero__container">
          <div className="ita-hero__copy">
            <p className="ita-hero__eyebrow">AI Strategy &amp; Advisory</p>

            <h1 className="ita-hero__title">
              Turn AI Ambition Into<br />
              <span className="ita-hero__title--gold">Business Results.</span>
            </h1>

            <p className="ita-hero__tagline">
              Strategy · Readiness · Governance · Scale
            </p>

            <p className="ita-hero__desc">
              Most organisations struggle with AI adoption because technology moves faster than
              business readiness. Bairees helps companies build a practical AI strategy aligned
              with business goals, operational priorities, data readiness, and measurable outcomes.
            </p>

            <p className="ita-hero__desc">
              We work with leadership teams to move beyond experimentation and create scalable,
              responsible, execution-focused AI transformation programs.
            </p>

            <div className="ita-hero__actions">
              <a href="/contact">
                <button className="ita-btn ita-btn--primary">
                  Build Your AI Roadmap <IoArrowForwardOutline size={16} />
                </button>
              </a>
              <a href="/contact">
                <button className="ita-btn ita-btn--ghost">
                  View Our Approach
                </button>
              </a>
            </div>
          </div>

          <div className="ita-hero__aside">
            {[
              { icon: <MdOutlinePsychology size={22} />, label: "Discipline", value: "AI Strategy & Advisory" },
              { icon: <FiTarget size={22} />, label: "Focus", value: "Measurable Business Outcomes" },
              { icon: <IoPeopleOutline size={22} />, label: "Model", value: "Leadership to Execution" },
            ].map((c, i) => (
              <div className="ita-hero__card" key={i}>
                <div className="ita-hero__card-icon">{c.icon}</div>
                <div className="ita-hero__card-label">{c.label}</div>
                <div className="ita-hero__card-value">{c.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── STATS BAR ─────────────────────────────────────── */}
      <div className="ita-stats" ref={statsRef}>
        <div className="ita-stats__inner">
          {stats.map((s, i) => (
            <React.Fragment key={i}>
              <StatCard {...s} delay={i * 150} inView={statsVisible} />
              {i < stats.length - 1 && <div className="ita-stats__divider" />}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* ── WHAT WE HELP YOU ACHIEVE ─────────────────────── */}
      <section className="ita-section ita-section--alt">
        <div className="ita-section__container">
          <div className="ita-section__header">
            <p className="ita-eyebrow">What We Help You Achieve</p>
            <h2 className="ita-section__title">From Strategy to Scalable AI</h2>
            <p className="ita-section__subtitle">
              Every engagement is scoped around the outcomes that create lasting competitive advantage —
              not just technology deployments.
            </p>
          </div>

          <div className="ita-deliverables">
            {achievements.map((a, i) => (
              <div className="ita-deliverable" key={i}>
                <span className="ita-deliverable__icon">{a.icon}</span>
                <span className="ita-deliverable__text">{a.title}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── APPROACH ─────────────────────────────────────── */}
      <section className="ita-framework">
        <div className="ita-framework__container">
          <div className="ita-framework__header">
            <p className="ita-eyebrow ita-eyebrow--light">Our Methodology</p>
            <h2 className="ita-framework__title">Our AI Advisory Approach</h2>
          </div>

          {/* Inline 5-step approach — swap for <img src={ai} /> when ready */}
          <div className="ita-approach__track">
            {approachSteps.map((s, i) => (
              <div className="ita-approach__step" key={i}>
                <div className="ita-approach__step-head">
                  <span className="ita-approach__num">{s.number}</span>
                  <span className="ita-approach__icon">{s.icon}</span>
                </div>
                <div className="ita-approach__step-body">
                  <div className="ita-approach__title">{s.title}</div>
                  <div className="ita-approach__desc">{s.desc}</div>
                </div>
                {i < approachSteps.length - 1 && (
                  <div className="ita-approach__connector" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY BAIREES ────────────────────────────────────── */}
      <section className="ita-section">
        <div className="ita-section__container">
          <div className="ita-two-col">

            {/* Why list */}
            <div>
              <p className="ita-eyebrow">Why Bairees</p>
              <h2 className="ita-panel__title">The Bairees AI Difference</h2>
              <ul className="ita-list">
                {whyBairees.map((w, i) => (
                  <li className="ita-list__item" key={i}>
                    <span className="ita-list__icon">{w.icon}</span>
                    <span className="ita-list__text">{w.text}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Differentiator chips */}
            <div className="ita-diff__panel">
              <p className="ita-eyebrow">Our Promise</p>
              <h2 className="ita-panel__title">What Sets Us Apart</h2>
              <div className="ita-diff__grid">
                {differentiators.map((d, i) => (
                  <div className="ita-diff__card" key={i}>
                    <IoCheckmarkCircleOutline size={22} className="ita-diff__check" />
                    <span className="ita-diff__text">{d}</span>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── CTA ────────────────────────────────────────────── */}
      <section className="ita-why">
        <div className="ita-why__container">
          <div className="ita-ai-cta">
            <p className="ita-eyebrow ita-eyebrow--light">Get Started</p>
            <h2 className="ita-ai-cta__title">Ready to Build Your AI Roadmap?</h2>
            <p className="ita-ai-cta__desc">
              Transform fragmented AI initiatives into scalable business value with a clear strategy,
              governance framework, and execution plan.
            </p>

            <div className="ita-hero__actions" style={{ marginTop: 0 }}>
              <a href="/contact">
                <button className="ita-btn ita-btn--primary">
                  Schedule Consultation <IoArrowForwardOutline size={16} />
                </button>
              </a>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default AiAdvisory;